import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, PageHeader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import { ProgramCourse } from "@/components/course/ProgramCourse";
import { CourseUnits, openFile } from "@/components/course/CourseUnits";
import { AddItemDialog } from "@/components/course/AddItemDialog";
import { CourseHeader } from "@/pages/student/StudentCourse";
import { PROGRAM_LABELS } from "@/lib/calendar";
import { toast } from "sonner";

export function TutorCourses() {
  const [program, setProgram] = useState(() => localStorage.getItem("ec_course_prog") || "jmss");
  const navigate = useNavigate();
  useEffect(() => { localStorage.setItem("ec_course_prog", program); }, [program]);
  return (
    <div className="animate-fade-up" data-testid="tutor-courses-page">
      <PageHeader title="Courses" subtitle="Open a program, then a subject, then a lesson. Students see exactly this layout."
        right={
          <Select value={program} onValueChange={setProgram}>
            <SelectTrigger data-testid="courses-program-select" className="h-10 w-52 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(PROGRAM_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        } />
      <ProgramCourse key={program} program={program} editable defaultOpen onOpen={(it) => (it.kind === "homework" ? navigate(`/tutor/homework/${it.homework_id}/review`) : openFile(it))} />
    </div>
  );
}

export default function TutorCourse() {
  const { program, section } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [adding, setAdding] = useState(null);
  const load = useCallback(() => api.get(`/courses/${program}/${section}`).then((r) => setCourse(r.data)).catch(() => navigate("/tutor/courses")), [program, section, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!course) return <Loading />;
  const addUnit = async () => {
    const t = window.prompt("Lesson name, e.g. Persuasive Writing");
    if (!t?.trim()) return;
    await api.post(`/courses/${program}/${section}/units`, { title: t }); toast.success("Lesson added."); load();
  };
  const open = (it) => (it.kind === "homework" ? navigate(`/tutor/homework/${it.homework_id}/review`) : openFile(it));
  return (
    <div className="animate-fade-up" data-testid="tutor-course-page">
      <CourseHeader course={course} back="/tutor/courses" />
      <div className="mb-4 flex justify-end">
        <Button onClick={addUnit} data-testid="add-unit-btn" className="rounded-xl ember-gradient font-bold"><Plus className="mr-1.5 h-4 w-4" /> Add lesson</Button>
      </div>
      <CourseUnits course={course} editable onOpen={open} onChanged={load} onAddItem={setAdding} />
      <AddItemDialog unit={adding} course={course} onClose={() => setAdding(null)} onSaved={() => { setAdding(null); load(); }} />
    </div>
  );
}
